/**
 * U0 · the inspector for a DECISION, or for one of its LANEs.
 *
 * A decision says what is decided; each lane is named by its condition, in the record's
 * words — "Condition A — within AQL". Which lane is taken is settled at execution; the master
 * record only holds what the choice is between.
 *
 * 2026-10-13 · and what it is decided ON: one sampling or parameter-record step before it in the
 * same operation. A sample decides Pass / Fail, a reading Within / Outside criteria; choosing the
 * step makes the decision's two lanes, one per outcome (the server's decision_basis.py — the list
 * offered here is only a convenience, the server refuses anything else).
 */
import React, { useMemo } from "react";
import { Alert, Flex, Input, Select, Tag, Typography } from "antd";
import { GitFork, Link2, Split } from "lucide-react";

const { Text } = Typography;

/** The step types a decision may depend on, and what each one decides. Mirrors the server. */
export const DECISION_BASIS = {
  SAMPLE: { outcomes: ["PASS", "FAIL"], label: "Sample", how: "the sample's result — the operator states Pass or Fail at execution, signed" },
  PARAMETER_RECORD: { outcomes: ["WITHIN", "OUTSIDE"], label: "Parameter record", how: "the reading against its limit — taken automatically at execution" },
};
export const OUTCOME_LABEL = { PASS: "Pass", FAIL: "Fail", WITHIN: "Within criteria", OUTSIDE: "Outside criteria" };
export const OUTCOME_TONE = { PASS: "green", WITHIN: "green", FAIL: "red", OUTSIDE: "red" };

const count = (nodes) => {
  let n = 0;
  const walk = (list) => (list || []).forEach((c) => { if (c.kind === "STEP") n += 1; walk(c.children); });
  walk(nodes);
  return n;
};

/** The steps worked before `code` in the operation, in order, not inside the decision itself.
 *  A step inside a repeat qualifies only for a decision inside that same repeat. */
export function decisionCandidates(operation, code) {
  const seen = [];
  let mine = null;
  const walk = (list, loops) => {
    for (const c of list || []) {
      if (mine) return;
      if (c.code === code) { mine = loops; return; }
      if (c.kind === "STEP" && DECISION_BASIS[c.step_type]) seen.push({ c, loops });
      walk(c.children, c.kind === "LOOP" ? [...loops, c.code] : loops);
    }
  };
  walk(operation?.children, []);
  return seen.filter(({ loops }) => loops.every((l) => (mine || []).includes(l))).map(({ c }) => c);
}

export default function DecisionForm({ step, operation, nodesByCode = {}, readOnly = false, showErrors = false, onChange, onEditNode }) {
  const isLane = step?.kind === "LANE";
  const candidates = useMemo(() => (isLane || !step ? [] : decisionCandidates(operation, step.code)),
                             [operation, step, isLane]);
  if (!step) return null;
  const set = (patch) => !readOnly && onChange?.(patch);
  const lanes = isLane ? [] : (step.children || []).filter((c) => c.kind === "LANE");
  const parent = isLane
    ? (nodesByCode[step.parent]
       || Object.values(nodesByCode).find((n) => n.kind === "DECISION" && (n.children || []).some((c) => c.code === step.code)))
    : null;
  const dep = step.depends_on ? (nodesByCode[step.depends_on] || candidates.find((c) => c.code === step.depends_on)) : null;
  const basis = DECISION_BASIS[dep?.step_type || step.basis] || null;
  // Staged but not saved yet: the lanes still carry the old outcomes until the server fits them.
  const fitted = basis && lanes.length === basis.outcomes.length
    && lanes.every((l, i) => l.outcome === basis.outcomes[i]);

  return (
    <Flex vertical gap={10} className="u0-stepform">
      <div className="u0-stepid" data-family="structure">
        <input className="u0-stepid-title" key={`t-${step.code}`} disabled={readOnly}
               placeholder={isLane ? "The condition this lane is taken on" : "What is decided here"}
               defaultValue={isLane ? step.label : step.title}
               onBlur={(e) => {
                 const cur = (isLane ? step.label : step.title) || "";
                 if (e.target.value !== cur) set(isLane ? { label: e.target.value } : { title: e.target.value });
               }} />
        <div className="u0-stepid-row">
          <span className="dz-code">{step.code}</span>
          <span className="pos">{isLane ? "lane" : "decision"}{parent ? ` of ${parent.code}` : ""}</span>
        </div>
      </div>

      {isLane ? (
        <Flex vertical gap={6}>
          {step.outcome ? (
            <Text style={{ fontSize: "calc(13px * var(--dz-fs, 1))" }}>
              Taken when the result is{" "}
              <Tag color={OUTCOME_TONE[step.outcome]} style={{ marginInlineEnd: 0 }}>{OUTCOME_LABEL[step.outcome] || step.outcome}</Tag>
              {parent?.depends_on ? <> — of <span className="dz-code">{parent.depends_on}</span></> : null}
            </Text>
          ) : null}
          <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
            <Split size={12} style={{ verticalAlign: -2 }} /> {count(step.children)} step{count(step.children) === 1 ? "" : "s"} run
            when this lane is taken. Add them in the sequence, inside the lane.
          </Text>
        </Flex>
      ) : (
        <>
          <Flex vertical gap={6}>
            {/* 2026-10-22 (owner) · required: a decision is not saved without the step it is decided on. */}
            <span className="dz-eyebrow"><Link2 size={11} style={{ verticalAlign: -1 }} /> Depends on — the step whose result decides
              {!readOnly ? <span className="u0-req" aria-label="required"> *</span> : null}</span>
            <Select
              size="middle" allowClear showSearch optionFilterProp="search" disabled={readOnly}
              status={showErrors && !readOnly && !step.depends_on ? "error" : undefined}
              value={step.depends_on || undefined}
              placeholder={candidates.length ? "Choose the sampling or parameter step" : "No sampling or parameter step before this decision"}
              onChange={(v) => set({ depends_on: v || null })}
              options={candidates.map((c) => ({
                value: c.code,
                search: `${c.code} ${c.title || ""}`,
                label: (
                  <span>
                    <span className="dz-code">{c.code}</span>{" "}{c.title || "Untitled step"}{" "}
                    <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                      · {DECISION_BASIS[c.step_type].label}
                    </Text>
                  </span>
                ),
              }))}
            />
            {basis ? (
              <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                Decided by {basis.how}.
              </Text>
            ) : candidates.length ? (
              <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                Choose the step, then Save — the two lanes are fitted to its result.
              </Text>
            ) : (
              <Alert type="warning" showIcon style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}
                     message="Only a sampling step (Pass / Fail) or a parameter record step (Within / Outside criteria) of this operation, worked before the decision, can be depended on." />
            )}
          </Flex>

          <Flex vertical gap={6}>
            <span className="dz-eyebrow"><GitFork size={11} style={{ verticalAlign: -1 }} /> Lanes — one is taken</span>
            {basis && !fitted ? (
              <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                Saving makes two lanes: {basis.outcomes.map((o) => OUTCOME_LABEL[o]).join(" and ")}. Steps already in a lane stay in it.
              </Text>
            ) : null}
            {lanes.map((l) => (
              <div key={l.code} className="u0-lanerow">
                <span className="dz-code">{l.code}</span>
                {l.outcome ? <Tag color={OUTCOME_TONE[l.outcome]} style={{ marginInlineEnd: 0 }}>{OUTCOME_LABEL[l.outcome] || l.outcome}</Tag> : null}
                <Input size="small" defaultValue={l.label} key={`l-${l.code}-${l.label}`} disabled={readOnly}
                       placeholder="The condition, in the record's words"
                       onBlur={(e) => e.target.value !== (l.label || "") && onEditNode?.(l.code, { label: e.target.value })} />
                <span className="n">{count(l.children)}</span>
              </div>
            ))}
            <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
              Which lane is taken is settled at execution from the result of the step this decision depends on.
              The lane not taken is recorded as skipped.
            </Text>
          </Flex>
        </>
      )}
    </Flex>
  );
}
